/**
 * Global Radio player — ONE <audio> element for every real (DB) song surface.
 * Plays the existing R2-backed public media URL (song.audio_url). No second
 * storage system. Provides play/pause, seek, volume, progress + now-playing.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Pause, Play, Volume2, VolumeX, Music4 } from "lucide-react";
import SongStarRating from "@/components/ratings/SongStarRating";
import SongShareButton from "@/components/sharing/SongShareButton";

const Ctx = createContext(null);
const fmt = (s) => (!isFinite(s) || s <= 0 ? "0:00" : `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`);

export function RadioPlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(0.85);
  const [error, setError] = useState(null);

  useEffect(() => {
    const a = new Audio(); a.preload = "metadata"; a.volume = 0.85; audioRef.current = a;
    a.addEventListener("timeupdate", () => setTime(a.currentTime));
    a.addEventListener("durationchange", () => setDuration(a.duration));
    a.addEventListener("play", () => setPlaying(true));
    a.addEventListener("pause", () => setPlaying(false));
    a.addEventListener("ended", () => setPlaying(false));
    a.addEventListener("error", () => { setPlaying(false); setError("This song's audio could not be loaded."); });
    return () => { a.pause(); a.src = ""; };
  }, []);

  const play = useCallback(async (song) => {
    const a = audioRef.current; if (!a || !song?.audio_url) return;
    setError(null);
    if (current?.id === song.id) { if (a.paused) { try { await a.play(); } catch { /* autoplay blocked */ } } else a.pause(); return; }
    setCurrent(song); setTime(0); setDuration(song.duration_seconds || 0);
    a.src = song.audio_url; a.load();
    try { await a.play(); } catch { /* autoplay blocked — user presses play */ }
  }, [current]);

  const toggle = useCallback(() => { const a = audioRef.current; if (!a || !current) return; if (a.paused) a.play().catch(() => {}); else a.pause(); }, [current]);
  const seek = useCallback((t) => { const a = audioRef.current; if (a && isFinite(t)) { a.currentTime = t; setTime(t); } }, []);
  const setVolume = useCallback((v) => { const a = audioRef.current; const vv = Math.max(0, Math.min(1, v)); if (a) a.volume = vv; setVolumeState(vv); }, []);

  const value = useMemo(() => ({ current, playing, time, duration, volume, error, play, toggle, seek, setVolume, isCurrent: (s) => s && current?.id === s.id }),
    [current, playing, time, duration, volume, error, play, toggle, seek, setVolume]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useRadioPlayer() { return useContext(Ctx); }

/** Compact play/pause control for song rows/cards. Renders nothing without audio. */
export function RadioPlayButton({ song, size = "sm", className = "" }) {
  const p = useRadioPlayer();
  if (!p || !song?.audio_url) return null;
  const active = p.isCurrent(song) && p.playing;
  const dim = size === "lg" ? "h-14 w-14" : size === "md" ? "h-10 w-10" : "h-9 w-9";
  return (
    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); p.play(song); }} data-testid={`radio-play-${song.id}`} data-playing={active ? "true" : "false"}
      aria-label={active ? `Pause ${song.title}` : `Play ${song.title}`}
      className={`${dim} shrink-0 flex items-center justify-center border transition-colors ${active ? "border-gold bg-gold text-black shadow-[0_0_18px_rgba(212,175,55,0.45)]" : "border-gold/50 text-gold hover:bg-gold/10"} ${className}`}>
      {active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
    </button>
  );
}

/** Now Playing hero — artwork · title · songwriter · transport · seek · volume · stars · share. */
export function RadioNowPlaying({ song, testId = "now-playing-hero" }) {
  const p = useRadioPlayer();
  const s = p?.current || song;
  if (!s) return null;
  const isCur = p?.isCurrent(s);
  const dur = (isCur && p.duration) || s.duration_seconds || 0;
  const t = isCur ? p.time : 0;
  return (
    <div className="border border-gold/30 bg-matte-card p-5 md:p-6" data-testid={testId}>
      <div className="flex items-start gap-4 md:gap-6 flex-wrap md:flex-nowrap">
        {s.artwork_url
          ? <img src={s.artwork_url} alt="" className="h-24 w-24 md:h-32 md:w-32 object-cover border border-gold/30 shrink-0" data-testid="now-playing-artwork" />
          : <div className="h-24 w-24 md:h-32 md:w-32 border border-gold/30 bg-black/50 flex items-center justify-center text-gold/60 shrink-0"><Music4 className="h-8 w-8" /></div>}
        <div className="min-w-0 flex-1">
          <div className="font-display uppercase text-[11px] tracking-[0.35em] text-gold">{isCur && p.playing ? "Now Playing" : "Now Playing · Paused"}</div>
          <div className="mt-1 text-white font-display uppercase tracking-wider text-xl md:text-2xl truncate" data-testid="now-playing-title">{s.title}</div>
          <div className="text-white/50 text-sm">by {s.owner_name}</div>
          {s.audio_url ? (
            <div className="mt-4 space-y-3" data-testid="now-playing-transport">
              <div className="flex items-center gap-3">
                <RadioPlayButton song={s} size="lg" />
                <span className="text-[11px] tabular-nums text-white/60 w-10 text-right" data-testid="now-playing-time">{fmt(t)}</span>
                <input type="range" min={0} max={dur || 0} step={0.1} value={Math.min(t, dur || 0)} onChange={(e) => p.seek(parseFloat(e.target.value))} disabled={!isCur || !dur}
                  data-testid="now-playing-seek" aria-label="Seek" className="flex-1 accent-[#d4af37] h-1 cursor-pointer" />
                <span className="text-[11px] tabular-nums text-white/60 w-10" data-testid="now-playing-duration">{fmt(dur)}</span>
                <button type="button" onClick={() => p.setVolume(p.volume > 0 ? 0 : 0.85)} aria-label="Mute" className="text-white/60 hover:text-gold">
                  {p.volume > 0 ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                </button>
                <input type="range" min={0} max={1} step={0.01} value={p.volume} onChange={(e) => p.setVolume(parseFloat(e.target.value))} data-testid="now-playing-volume" aria-label="Volume" className="w-24 accent-[#d4af37] h-1 cursor-pointer" />
              </div>
              {p.error && isCur && <div className="text-[11px] text-amber-300 uppercase tracking-widest">{p.error}</div>}
            </div>
          ) : (
            <div className="mt-3 text-[10px] uppercase tracking-widest text-white/40">Audio not attached yet — metadata only</div>
          )}
          <div className="mt-4 flex flex-wrap items-end gap-4" data-testid="nowplaying-actions">
            <SongStarRating song={s} size="lg" />
            <SongShareButton song={s} compact />
          </div>
        </div>
      </div>
    </div>
  );
}
